import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/config/api";
import { queryKeys } from "@/hooks/queries";
import type { ApiResponse } from "@/types";
import type { Challenge, ChallengeWithScenarios } from "@/types/challenges";

interface ChallengesApiResponse extends ApiResponse {
  challenges: Challenge[];
}

interface ChallengeApiResponse extends ApiResponse {
  challenge: Challenge;
  scenarios: ChallengeWithScenarios["scenarios"];
}

interface CreateChallengeApiResponse extends ApiResponse {
  challenge: Challenge;
}

export function useMyChallenges() {
  return useQuery({
    queryKey: queryKeys.challenges,
    queryFn: () => api.get<ChallengesApiResponse>("/api/challenges"),
    select: (data) => data.challenges,
  });
}

export function useChallenge(challengeId: string) {
  return useQuery({
    queryKey: [...queryKeys.challenges, challengeId],
    queryFn: () => api.get<ChallengeApiResponse>(`/api/challenges/${challengeId}`),
    select: (data) => ({ challenge: data.challenge, scenarios: data.scenarios }),
    enabled: !!challengeId,
  });
}

export function useCreateChallenge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (count: number = 5) =>
      api.post<CreateChallengeApiResponse>("/api/challenges", { count }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.challenges });
    },
  });
}

export function useSubmitChallengeResult() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ challengeId, answers }: { challengeId: string; answers: { scenarioId: string; selectedOptionId: string }[] }) =>
      api.post<{ success: boolean; challenge: Challenge }>(`/api/challenges/${challengeId}/result`, {
        answers,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.challenges });
    },
  });
}
